import React, { useState, useRef, useEffect } from "react";
import { Phone, ChevronDown, Car, MapPin, ShieldCheck, BadgeCheck, ArrowRight, HelpCircle, FileText } from "lucide-react";

const MEGA_IMG = "https://static.prod-images.emergentagent.com/jobs/316a32c4-cbe9-4f91-b455-89f832706401/images/072ef58fa307f630bdf1941592c77c0f1830eb05fcc616a9f13ce20046aee677.png";

export default function ShopHeader({ shop, T }) {
  const [panel, setPanel] = useState(null);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setPanel(null); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const phone = shop.contact?.phone || shop.phone;
  const wa = shop.contact?.whatsapp;
  const locs = shop.locations || [];
  const vehicles = shop.vehicles || [];
  const hasConditions = Object.keys(shop.conditions || {}).length > 0;
  const hasFaq = (shop.faq || []).length > 0;
  const hasTrust = shop.verified || shop.stripe_enabled || shop.insurance;
  const cities = shop.cities || [];

  const NAV_ITEMS = [
    { key: "decouvrir", label: "Découvrir", show: true },
  ].filter((i) => i.show);

  const navLink = `inline-flex items-center gap-1 px-3.5 h-9 rounded-full text-xs font-semibold transition-colors ${T.dark ? "text-slate-300 hover:text-white hover:bg-white/5" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"}`;
  const linkRow = `flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${T.dark ? "hover:bg-white/5 text-slate-200" : "hover:bg-slate-50 text-slate-700"}`;

  const DISCOVER_LINKS = [
    ["#vehicules", "Notre flotte", Car, vehicles.length > 0],
    ["#agences", "Nos agences", MapPin, locs.length > 0],
    ["#garanties", "Garanties", ShieldCheck, hasTrust],
    ["#conditions", "Conditions", FileText, hasConditions],
    ["#faq", "FAQ", HelpCircle, hasFaq],
  ].filter(([, , , show]) => show);

  return (
    <header ref={ref} className={`sticky top-0 z-40 ${T.nav}`} data-testid="shop-nav">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 h-16 flex items-center gap-3">
        <a href="#accueil" className="flex items-center gap-2.5 shrink-0" data-testid="shop-header-logo">
          {shop.logo ? (
            <img src={shop.logo} alt={shop.name} className="h-9 w-9 rounded-lg object-contain bg-white p-0.5" />
          ) : (
            <span className={`h-9 w-9 rounded-lg flex items-center justify-center text-sm font-black ${T.accentBox}`}>{(shop.name || "?").charAt(0)}</span>
          )}
          <span className="text-sm font-bold truncate max-w-[160px]" data-testid="shop-name">{shop.name}</span>
        </a>

        <nav className="hidden lg:flex items-center gap-0.5 ml-4">
          {NAV_ITEMS.map((item) => (
            <button key={item.key} onClick={() => setPanel(panel === item.key ? null : item.key)}
              className={`${navLink} ${panel === item.key ? (T.dark ? "bg-white/10 text-white" : "bg-slate-100 text-slate-900") : ""}`}
              data-testid={`shop-mega-${item.key}`}>
              {item.label} <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${panel === item.key ? "rotate-180" : ""}`} />
            </button>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          {phone && (
            <a href={`tel:${phone}`} className={`hidden sm:inline-flex items-center gap-2 px-4 h-10 text-xs font-bold transition-colors ${T.ghost}`} data-testid="header-call-btn">
              <Phone className="h-3.5 w-3.5" /> {phone}
            </a>
          )}
          {wa && (
            <a href={`https://wa.me/${wa.replace(/\D/g, "")}`} target="_blank" rel="noreferrer"
              className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#25D366] text-white hover:scale-105 transition-transform"
              data-testid="header-whatsapp-btn" aria-label="WhatsApp">
              <i className="fab fa-whatsapp text-lg" />
            </a>
          )}
          <a href="#reservation" onClick={() => setPanel(null)} className={`inline-flex items-center gap-1.5 px-5 h-10 text-xs font-bold transition-all hover:scale-[1.03] ${T.btn}`} data-testid="header-cta">
            Réserver <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>

      {panel && <div className="hidden lg:block fixed inset-0 top-16 z-[-1] bg-black/25 backdrop-blur-sm cursor-pointer" style={{ animation: "jnFade .25s both" }} onClick={() => setPanel(null)} data-testid="mega-blur-overlay" />}
      {panel && (
        <div className={`hidden lg:block absolute inset-x-0 top-16 border-b shadow-2xl ${T.dark ? "bg-[#0D1326] border-white/10 shadow-black/50" : "bg-white border-slate-200 shadow-slate-300/40"}`}
          style={{ animation: "jnPanelIn .3s cubic-bezier(.22,1,.36,1) both" }} data-testid="shop-mega-panel">
          <div className="max-w-6xl mx-auto px-8 py-8">

            {panel === "decouvrir" && (
              <div className="grid grid-cols-[1.1fr,1fr,340px] gap-10 items-start" data-testid="mega-panel-decouvrir">
                <div>
                  <div className="flex items-center gap-3">
                    {shop.logo && <img src={shop.logo} alt="" className="h-12 w-12 rounded-xl object-contain bg-white p-1" />}
                    <div>
                      <div className="text-base font-bold">{shop.name}</div>
                      {shop.verified && <div className={`inline-flex items-center gap-1 text-[11px] font-bold ${T.dark ? "text-emerald-300" : "text-emerald-600"}`}><BadgeCheck className="h-3 w-3" /> Entreprise vérifiée</div>}
                    </div>
                  </div>
                  <p className={`mt-4 text-sm leading-relaxed ${T.muted}`}>
                    Location de véhicules{cities.length ? ` à ${cities.join(", ")}` : ""} — réservez en ligne en quelques clics, avec paiement sécurisé et confirmation immédiate.
                  </p>
                  {vehicles.length > 0 && (
                    <div className={`mt-4 flex items-center gap-4 text-xs font-semibold ${T.muted}`}>
                      <span className="inline-flex items-center gap-1.5"><Car className={`h-3.5 w-3.5 ${T.icon}`} /> {vehicles.length} véhicule{vehicles.length > 1 ? "s" : ""}</span>
                      {locs.length > 0 && <span className="inline-flex items-center gap-1.5"><MapPin className={`h-3.5 w-3.5 ${T.icon}`} /> {locs.length} agence{locs.length > 1 ? "s" : ""}</span>}
                    </div>
                  )}
                </div>
                <div className="space-y-0.5">
                  {DISCOVER_LINKS.map(([href, lbl, Icon]) => (
                    <a key={href} href={href} onClick={() => setPanel(null)} className={linkRow} data-testid={`mega-link-${href.slice(1)}`}>
                      <span className="inline-flex items-center gap-2.5"><Icon className={`h-4 w-4 ${T.icon}`} /> {lbl}</span>
                      <ArrowRight className="h-3.5 w-3.5 opacity-40" />
                    </a>
                  ))}
                </div>
                <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
                  <img src={MEGA_IMG} alt="Location de véhicule" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                </div>
              </div>
            )}

          </div>
        </div>
      )}
      <style>{`@keyframes jnPanelIn{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:none}}@keyframes jnFade{from{opacity:0}to{opacity:1}}`}</style>
    </header>
  );
}
